import { useCallback } from 'react';
import { NAVIGATION_KEYS } from 'src/interfaces/enums';

interface IRefElementProps {
  ref: {
    (element: HTMLElement | null): void;
    current: HTMLElement | null;
  };
}

interface IRegisterElementProps {
  startWithFocus?: boolean;
}

export const useListNavigationByKeyboard = () => {
  const setFocusToElement = useCallback((element?: HTMLElement) => {
    element?.focus();
  }, []);

  const focusToPrevSiblingItem = useCallback(() => {
    const item = document.activeElement as Element;
    setFocusToElement(item.previousElementSibling as HTMLElement);
  }, [setFocusToElement]);

  const focusToNextSiblingItem = useCallback(() => {
    const item = document.activeElement as Element;
    setFocusToElement(item.nextElementSibling as HTMLElement);
  }, [setFocusToElement]);

  const handleOnKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key.includes('Arrow')) {
        event.stopPropagation();
        event.preventDefault();
      }

      const defaultKeyDownActions = {
        [NAVIGATION_KEYS.ARROW_DOWN]: () => {
          focusToNextSiblingItem();
        },
        [NAVIGATION_KEYS.ARROW_UP]: () => {
          focusToPrevSiblingItem();
        },
        [NAVIGATION_KEYS.SPACE]: () => event.stopPropagation(),
        [NAVIGATION_KEYS.ENTER]: () => event.stopPropagation(),
      };

      defaultKeyDownActions[event.key]?.();
    },
    [focusToPrevSiblingItem, focusToNextSiblingItem],
  );

  const registerHTMLElement = useCallback(
    ({ startWithFocus = false }: IRegisterElementProps) => {
      const refElement: IRefElementProps = {
        ref: (element: HTMLElement | null) => {
          if (!element || element.dataset.navigation_registered) {
            return;
          }

          element.addEventListener('keydown', handleOnKeyDown);

          if (startWithFocus) {
            element.focus();
          }

          element.dataset.navigation_registered = 'true';

          Object.assign(refElement.ref, { current: element });
        },
      } as IRefElementProps;
      return refElement;
    },
    [handleOnKeyDown],
  );

  return {
    registerHTMLElement,
  };
};
